import {
  Box,
  Button,
  Center,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { useGoogleLogin } from "@react-oauth/google";
import AppleSignin from "react-apple-signin-auth";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { FaApple, FaFacebook, FaGoogle } from "react-icons/fa";
import { useQueryClient } from "react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import { trackEvent } from "../../../shared/analytics";
import { buildAuthRedirectPath, getAuthRedirect } from "../authRedirect";
import { Card } from "../components/Login/Card";
import { DividerWithText } from "../components/Login/DividerWithText";
import { Link as ChakraLink } from "../components/Login/Link";
import LoginForm from "../components/Login/LoginForm";
import { Logo } from "../components/shared/Logo";
import usePageMeta from "../../../shared/hooks/usePageMeta";

const Login = () => {
  usePageMeta(
    "Log in to Redwing",
    "Log in to Redwing to continue your comic reading orders and pick up where you left off.",
    "/login"
  );
  const { signIn, socialSignIn } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const redirectPath = getAuthRedirect(location);

  const onLoginSuccess = (method) => {
    trackEvent("login", { method });
    queryClient.refetchQueries("subscriptionCheck");
    navigate(redirectPath, { replace: true });
  };

  const onSubmit = (email, password) => {
    signIn(email, password)
      .then(() => onLoginSuccess("email"))
      .catch((err) => {
        trackEvent("login_failed", { method: "email" });
        toast({
          title: "Could not sign in.",
          description: "These credentials are not invalid. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (tokenResponse) {
        return socialSignIn(
          "google_web",
          tokenResponse.access_token,
          "web"
        ).then(() => onLoginSuccess("google"));
      } else {
        console.log("BANG");
      }
    },
  });

  const responseFacebook = (response) => {
    if (response.accessToken) {
      return socialSignIn("facebook", response.accessToken, "web").then(() => {
        onLoginSuccess("facebook");
      });
    } else {
      console.log("BANG");
    }
  };

  const responseApple = (response) => {
    if (response.authorization) {
      console.log(response);
      return socialSignIn("apple", response.authorization.id_token, "web").then(
        () => {
          onLoginSuccess("apple");
        }
      );
    }
  };

  const handleAppleLoginFailure = (error) => {
    console.log(error);
    if (error.error !== "popup_closed_by_user") {
      toast({
        title: "Could not sign in.",
        description: "These credentials are not invalid. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      bg={useColorModeValue("gray.50", "inherit")}
      minH="100vh"
      py="12"
      px={{ base: "4", lg: "8" }}
    >
      <Box maxW="md" mx="auto">
        <Center mx="auto" h="8" mb={{ base: "10", md: "20" }}>
          <Logo />
        </Center>
        <Heading textAlign="center" size="xl" fontWeight="extrabold">
          Sign in to your account
        </Heading>
        <Text mt="4" mb="8" align="center" maxW="md" fontWeight="medium">
          <Text as="span">Don&apos;t have an account?</Text>
          <Link to={buildAuthRedirectPath("/signup", redirectPath)}>
            <ChakraLink>Sign up now</ChakraLink>
          </Link>
        </Text>
        <Card>
          <LoginForm onSubmit={onSubmit} />
          <DividerWithText mt="6">or continue with</DividerWithText>
          <SimpleGrid mt="6" columns={3} spacing="3">
            <FacebookLogin
              appId="610550656819409"
              autoLoad={false}
              fields="name,email,picture"
              callback={responseFacebook}
              render={(renderProps) => (
                <Button
                  onClick={renderProps.onClick}
                  color="currentColor"
                  variant="outline"
                >
                  <VisuallyHidden>Login with Facebook</VisuallyHidden>
                  <FaFacebook />
                </Button>
              )}
            />
            <Button
              color="currentColor"
              variant="outline"
              onClick={googleLogin}
            >
              <VisuallyHidden>Login with Google</VisuallyHidden>
              <FaGoogle />
            </Button>
            <AppleSignin
              authOptions={{
                clientId: "app.theredwing.comics.sid",
                scope: "email name",
                redirectURI: "https://redwing.app/login",
                state: "",
                nonce: "nonce",
                usePopup: true,
              }}
              noDefaultStyle={false}
              onSuccess={responseApple} // default = undefined
              onError={handleAppleLoginFailure} // default = undefined
              render={(props) => (
                <Button color="currentColor" variant="outline" {...props}>
                  <VisuallyHidden>Login with Apple</VisuallyHidden>
                  <FaApple />
                </Button>
              )}
            />
          </SimpleGrid>
        </Card>
      </Box>
    </Box>
  );
};

export default Login;
